/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 뷰의 목록 클래스를 정의한다.
 *
 * @file /src/components/viewer/List.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 6. 26.
 */
import * as parent from '@components/grid/List';
import { Viewer } from '@components/viewer/Viewer';
import { View } from '@components/viewer/View';
import { Panel } from '@components/viewer/Panel';
import { Component } from '@base/Component';
import { Record } from '@models/store/Record';

export interface Listeners<T extends parent.List> extends parent.Listeners<T> {
    selectionChange?: never;
}

export interface Properties<T extends parent.List> extends parent.Properties<T> {
    /**
     * 선택가능여부
     */
    selectable?: boolean | ((record: Record) => boolean);

    /**
     * 하단 페이지네이션 추가 버튼
     */
    bottombar?: (string | Component)[];

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export class List extends parent.List {
    /**
     * 뷰의 목록 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Properties<List> = null) {
        super({
            ...properties,
            selection: {
                selectable: properties.selectable ?? true,
                deselectable: false,
                multiple: false,
            },
            layout: 'fit',
            border: false,
            autoLoad: false,
            flex: 1,
            topbar: null,
            topbars: null,
            bottombar: null,
            bottombars: null,
            pagination: null,
            listeners: {
                ...properties.listeners,
                selectionChange: async (selections, me) => {
                    const panel = this.getPanel();
                    if (panel === null) {
                        return;
                    }

                    if (selections.length === 0) {
                        await panel.hide();
                        await this.getView().setPath(null);
                    } else {
                        const record = selections[0];
                        await this.getView().setPath(this.getRecordUrl(record));
                        await panel.show();
                        await panel.update?.(record);
                    }
                },
            },
        });
    }

    /**
     * URL에 사용할 레코드 PK 값을 가져온다.
     *
     * @param record - 레코드
     * @returns URL에 사용할 레코드 PK 값
     */
    getRecordUrl(record: Record): string {
        const pk = this.getStore().primaryKeys[0];
        return record.get(pk); // @todo 여러개일떄 . / 등등
    }

    /**
     * 뷰어를 가져온다.
     *
     * @returns 뷰어
     */
    getViewer(): Viewer | null {
        return this.getView().getViewer();
    }

    /**
     * 뷰 패널을 가져온다.
     *
     * @returns 뷰패널
     */
    getView(): View {
        return this.getParent<View>();
    }

    /**
     * 상세패널을 가져온다.
     *
     * @returns 상세패널
     */
    getPanel(): Panel | null {
        return this.getView().getPanel() ?? null;
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        await super.renderState();
    }
}
